import { useEffect, useState } from "react";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";

// Settings → Website → Registration Form → "Conversion tracking".
// Per-business ad plumbing for the public signup page:
//   meta_pixel_id / google_tag_id  → injected on the signup page itself
//   thankyou_redirect_url          → full-page signups bounce here on success
//   embed_allowed_origins          → postMessage targetOrigin allow-list
//   google_ads_* / ga4_measurement → conversion action + GA4 stream (Enhanced Conversions)
//   signup_webhook_url/secret      → server-side conversion POST (HMAC-signed)
// The webhook pair is intentionally the only part not shipped to the browser —
// public-operator-config must never select it.
const FIELDS = [
  ["meta_pixel_id",         "Meta Pixel ID",           "e.g. 1377457017012540", false],
  ["google_tag_id",         "Google Tag ID",           "e.g. GT-TBBPTF25 / G-XXXX / AW-XXXX", false],
  ["google_ads_conversion_id",    "Google Ads conversion ID",    "AW-XXXXXXXXXX — from the conversion action's tag", false],
  ["google_ads_conversion_label", "Google Ads conversion label", "the string after the / in that same tag", false],
  ["ga4_measurement_id",    "GA4 measurement ID",      "G-XXXXXXX — only if it's a different stream than the tag above", false],
  ["thankyou_redirect_url", "Thank-you redirect URL",  "https://yoursite.com/welcome/ (full-page signups only)", false],
  ["embed_allowed_origins", "Allowed embed origins",   "https://yoursite.com — comma-separated; where your embedded form may post events", false],
  ["signup_webhook_url",    "Conversion webhook URL",  "https://… — receives a signed POST on every signup", false],
  ["signup_webhook_secret", "Webhook signing secret",  "shared secret for the X-ISCoop-Signature HMAC", true],
];

// Owner-only pulls from the marketing-exports edge function. "customers" is the
// audience file (hashed email/phone → Google Customer Match / Meta Custom Audiences);
// the rest are read-only reports.
const EXPORTS = [
  ["customers",          "Customer audience",  "Hashed emails + phones, plan, value and the ad click that won them"],
  ["signups_by_zip",     "Signups by ZIP",     "Where signups come from and how many are still active"],
  ["funnel",             "Funnel drop-off",    "Where people abandon the signup form"],
  ["out_of_area_by_zip", "Out-of-area demand", "ZIPs people asked for that you don't service yet"],
  ["time_to_convert",    "Time to convert",    "Days from ad click to paying customer"],
  ["plan_mix_by_source", "Plan mix by source", "Which traffic source buys which plan"],
];

export function TrackingSettings({ org, showToast, onSaved }) {
  const isMobile = useIsMobile();
  // The webhook secret is never read from the organization row (or returned by
  // the API). Blank means "keep the stored secret" unless the owner explicitly
  // checks Remove below.
  const seed = () => Object.fromEntries(FIELDS.map(([k]) => [k, k === "signup_webhook_secret" ? "" : (org?.[k] || "")]));
  const [f, setF] = useState(seed);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [secretConfigured, setSecretConfigured] = useState(false);
  const [clearSecret, setClearSecret] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${SUPABASE_URL}/functions/v1/tracking-settings`, {
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` },
        });
        if (!r.ok) throw new Error(await r.text());
        const data = await r.json();
        if (cancelled) return;
        setF(Object.fromEntries(FIELDS.map(([k]) => [k, k === "signup_webhook_secret" ? "" : (data[k] || "")])));
        setSecretConfigured(!!data.signup_webhook_secret_configured);
      } catch (e) { console.error("tracking settings load failed", e); }
    })();
    return () => { cancelled = true; };
  }, [org?.id]);

  async function save() {
    const url = f.thankyou_redirect_url.trim();
    if (url && !/^https:\/\//i.test(url)) { showToast("Thank-you URL must start with https://", "error"); return; }
    const hook = f.signup_webhook_url.trim();
    if (hook && !/^https:\/\//i.test(hook)) { showToast("Webhook URL must start with https://", "error"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/functions/v1/tracking-settings`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          ...Object.fromEntries(FIELDS.map(([k]) => [k, f[k].trim() || null])),
          clear_webhook_secret: clearSecret,
        }),
      });
      const result = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(result.error || "Tracking settings update failed");
      setSecretConfigured(!!result.signup_webhook_secret_configured);
      setF((p) => ({ ...p, signup_webhook_secret: "" }));
      setClearSecret(false);
      showToast("Tracking settings saved");
      onSaved && onSaved();
    } catch (e) { console.error(e); showToast("Could not save tracking settings", "error"); }
    setSaving(false);
  }

  async function download(report, label) {
    setBusy(report);
    try {
      const r = await fetch(`${SUPABASE_URL}/functions/v1/marketing-exports?report=${report}&format=csv`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` },
      });
      // The endpoint is owner-only and returns JSON on failure — a blob saved blind
      // would hand the user a "CSV" containing an error message.
      if (!r.ok) {
        const msg = await r.json().catch(() => ({}));
        showToast(msg.error || `Could not export ${label}`, "error");
      } else {
        const url = URL.createObjectURL(await r.blob());
        const a = document.createElement("a");
        a.href = url;
        a.download = `${report}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (e) { console.error(e); showToast(`Could not export ${label}`, "error"); }
    setBusy("");
  }

  const inp = { width: "100%", padding: "9px 11px", border: "1px solid var(--border)", borderRadius: "var(--r-sm, 8px)", fontSize: 13, boxSizing: "border-box", background: "var(--surface)", color: "var(--text)" };
  const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 };

  if (!org) return null;
  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div className="card-title">📈 Conversion tracking</div>
      <div style={{ fontSize: 12.5, color: "var(--text2)", lineHeight: 1.6, margin: "2px 0 14px", maxWidth: 720 }}>
        Wire your ad platforms to real signups instead of page visits. Pixel IDs fire Meta/Google events on the
        signup page itself; the redirect and webhook let your website and ad tools see each completed signup —
        never with the customer's personal details. Adding the Google Ads conversion ID and label also turns on
        Enhanced Conversions, which recovers signups whose ad click the browser stopped tracking.
      </div>
      {/* phones: one column — two ~120px columns inside the padded settings pane made the labels wrap 3 lines and the placeholders unreadable */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0,1fr)" : "1fr 1fr", gap: 12 }}>
        {FIELDS.map(([k, label, ph, secret]) => (
          <div key={k} style={{ minWidth: 0, gridColumn: !isMobile && (k === "thankyou_redirect_url" || k === "embed_allowed_origins" || k === "signup_webhook_url") ? "1 / -1" : undefined }}>
            <label style={lbl}>{label}</label>
            <input style={inp} type={secret ? "password" : "text"} value={f[k]} placeholder={secret && secretConfigured ? "Configured — leave blank to keep it" : ph}
              onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} autoComplete="off" />
            {k === "signup_webhook_secret" && secretConfigured && (
              <label style={{ display:"flex", alignItems:"center", gap:7, marginTop:6, fontSize:11.5, color:"var(--text2)" }}>
                <input type="checkbox" checked={clearSecret} onChange={(e) => setClearSecret(e.target.checked)} />
                Remove the stored webhook secret when I save
              </label>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <button className="btn btn-gold" disabled={saving} onClick={save} style={isMobile ? { width: "100%" } : undefined}>{saving ? "Saving..." : "Save tracking settings"}</button>
      </div>

      <div style={{ borderTop: "1px solid var(--border)", margin: "18px 0 14px" }} />
      <div className="card-title" style={{ fontSize: 14 }}>📊 Audience &amp; reporting exports</div>
      <div style={{ fontSize: 12.5, color: "var(--text2)", lineHeight: 1.6, margin: "2px 0 12px", maxWidth: 720 }}>
        Downloads as CSV. Customer emails and phone numbers are exported <strong>hashed</strong>, which is the
        format Google and Meta want for audience matching — the raw details never leave the CRM.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0,1fr)" : "repeat(auto-fit, minmax(230px, 1fr))", gap: 8 }}>
        {EXPORTS.map(([key, label, blurb]) => (
          <button key={key} className="btn" disabled={!!busy} onClick={() => download(key, label)}
            style={{ textAlign: "left", padding: "10px 12px", height: "auto", lineHeight: 1.4, minWidth: 0, whiteSpace: isMobile ? "normal" : undefined }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{busy === key ? "Preparing…" : label}</div>
            <div style={{ fontSize: 11.5, color: "var(--text2)", fontWeight: 400 }}>{blurb}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
